import { useEffect, type ReactNode } from "react";

import { fanfare } from "@/shared/telegram";
import { Button, Celebration, Icon, type IconName } from "@/shared/ui";

/**
 * Пустой экран, экран ошибки и подтверждение успеха — в макете это один и тот
 * же блок `State` 350×420 (28:11, 28:21, 28:37), меняются только цвет, глиф и
 * тексты. Поэтому и в коде он один: три копии разъехались бы при первой правке.
 *
 * Кружок 96×96 собран так же, как круглые иконки меню: тёмная подложка с
 * бликом, тонкая граница и светящийся ореол наружу. Глиф при этом красится
 * тоном состояния и остаётся читаемым — в отличие от прежней сплошной заливки,
 * где он совпадал с фоном подложки.
 *
 * Движение (FE-13): кружок входит с перелётом, тексты поднимаются следом.
 * Отдельно объявляется `celebrate` — волна по кольцу, разлёт частиц и
 * тактильная фанфара (FE-14), всё однократно. Это НЕ то же самое, что тон
 * `profit`: зелёным покрашен и пустой экран «всё прочитано», а праздновать
 * там нечего. Празднуется только действие, которое пользователь совершил и
 * довёл до конца.
 */

export type StateTone = "info" | "profit" | "loss" | "warning";

/**
 * Тон задаётся свойством `color` — кружок, его ореол и волна салюта читают
 * `currentColor`. Так же устроены все круглые элементы пакета дизайна.
 */
const TONES: Record<StateTone, string> = {
  info: "text-status-info",
  profit: "text-status-profit",
  loss: "text-status-loss",
  warning: "text-status-warning",
};

interface ScreenStateProps {
  tone: StateTone;
  icon: IconName;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void } | undefined;
  children?: ReactNode;
  /** Завершённое действие: волна, салют и тактильная фанфара. Один раз. */
  celebrate?: boolean;
  /**
   * Акцент салюта — цвет робота, которого пользователь только что купил.
   * Приходит данными из админки, поэтому цвет здесь строкой, а не токеном.
   */
  accent?: string | undefined;
}

export function ScreenState({
  tone,
  icon,
  title,
  body,
  action,
  children,
  celebrate = false,
  accent,
}: ScreenStateProps) {
  useEffect(() => {
    if (!celebrate) return;
    // Отмена обязательна: без неё последний шаг фанфары догонит пользователя
    // уже на следующем экране, если он нажмёт CTA раньше, чем она доиграет.
    return fanfare();
  }, [celebrate]);

  return (
    <div className="stagger-children flex w-full flex-col items-center gap-4 px-5 py-10">
      <span
        className={`relative flex size-[96px] shrink-0 items-center justify-center ${TONES[tone]}`}
      >
        {celebrate ? (
          <>
            <span
              aria-hidden="true"
              className="animate-ring absolute inset-0 rounded-full border border-current"
            />
            <Celebration accent={accent} />
          </>
        ) : null}

        {/* Ореол вместо сплошной заливки: в новом языке круглый элемент
            светится наружу, а не залит цветом — иначе глиф внутри пришлось бы
            красить в цвет фона, и он снова стал бы невидимым. */}
        <span aria-hidden="true" className="halo absolute -inset-1 rounded-full" />

        <span
          className={[
            "animate-pop-in flex size-full items-center justify-center rounded-full",
            "border border-border-subtle bg-[image:var(--surface-round-icon)]",
          ].join(" ")}
        >
          <Icon name={icon} size={42} />
        </span>
      </span>

      <h1 className="text-h2 font-bold text-text-bright">{title}</h1>
      <p className="max-w-[310px] text-center text-small font-normal text-text-secondary">
        {body}
      </p>

      {children}

      {action ? (
        <Button size="cta" block onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
